/**
 * useLazyEntityDetail.ts
 *
 * Generic "click a row → fetch full entity by pid → open detail dialog"
 * hook shared by copilot list cards (Priority, Objective). List rows only
 * carry partial data, so the full entity is lazily fetched via TanStack
 * Query only once a pid is selected.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

export function useLazyEntityDetail<T>(
  queryKeyPrefix: string,
  fetchFn: (pid: string) => Promise<T>,
) {
  const [selectedPid, setSelectedPid] = useState<string | null>(null);

  const { data: entity, isLoading } = useQuery({
    queryKey: [queryKeyPrefix, selectedPid],
    queryFn: () => fetchFn(selectedPid as string),
    enabled: !!selectedPid,
  });

  const openPid = (pid: string) => setSelectedPid(pid);
  const close = () => setSelectedPid(null);

  return { entity, isLoading, openPid, selectedPid, close };
}
